import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import styles from './revert-dialog.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { DatasetAutomationDetails, DatasetDetails } from '@interfaces/central-monitoring';
import { AutomationTypes } from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { Logger } from '@utils/logger';
import { ShowError, ShowSuccess } from '@utils/notification';
import AutomationApi from '@apis/bff-api/central-monitoring/automation';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

interface IProps {
    dataset: DatasetDetails;
    automation: DatasetAutomationDetails;
    studyRef: string;
    onClose: () => void;
    automationType: AutomationTypes;
}

interface FormInputs {
    deleteReason: string | null;
}

const RevertDialog = ({ dataset, automation, studyRef, onClose, automationType }: IProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', { keyPrefix: 'monitoringToolkit.revertAutomation' });
    const { t: tCommon } = useTranslation('common');

    const { trigger: revert, isMutating: isStarting } = AutomationApi.useRevert(
        studyRef,
        automation.automationId,
        automationType,
    );

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const onSubmit = async (data: FormInputs) => {
        try {
            if (!data.deleteReason) return;

            const isReverting = await revert(data.deleteReason);

            if (!isReverting) {
                throw new Error('Call to revert automation failed.');
            }

            ShowSuccess(tRoot('actionsSuccessfulToast'));

            await DatasetApi.invalidateGetGrid(studyRef, automationType);
            await DatasetApi.invalidateGetAutomation(studyRef, dataset.id, automationType);

            onClose();
        } catch (error) {
            Logger.error(error, 'RevertDialog', 'onSubmit', 'Error reverting automation');
            ShowError(tCommon('errorMessage'));
        }
    };

    return (
        <DraggableDialog
            dataTestId="revert-dialog"
            title={t('revertGenerationConfirmation')}
            onSubmit={() => handleSubmit(onSubmit)()}
            onClose={onClose}
            primaryBtnLabel={t('revertGeneration')}
            primaryBtnDisabled={!isValid || !isDirty || isStarting}
            height={410}
            width={594}
        >
            <div className="k-d-flex-col k-w-full">
                <div>{t('revertMessage', { dataset: stringDateWithoutTimeFromUtc(dataset.date, 'DD-MMM-YYYY') })}</div>
                <div>{t('observationCount', { observationCount: automation.generatedObservationCount })}</div>
                <div>{t('reviewCount', { reviewCount: automation.childReviewLogCount })}</div>
                <div>
                    {t('actionCount', { actionCount: automation.childActionCount })}
                    {automationType === AutomationTypes.Site ? ` ${t('actionWarning')}` : ''}
                </div>
                <br />
                <TextAreaController
                    className={cn(styles.revertReasonTextarea)}
                    control={control}
                    name="deleteReason"
                    data-testid="revert-reason"
                    label={t('reason')}
                    maxLength={1200}
                    required
                />
            </div>
        </DraggableDialog>
    );
};

export default RevertDialog;
